import React, { createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';
import { PublicClientApplication, InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { fetchAuthConfig, buildMsalConfig, buildLoginRequest } from './authConfig';

const AuthContext = createContext({
  user: null,
  roles: [],
  isAdmin: false,
  isUser: false,
  getAccessToken: async () => null,
  login: () => {},
  logout: () => {},
  forceReLogin: () => {},
  authEnabled: false,
});

export const useAuth = () => useContext(AuthContext);

// Guards against multiple components independently triggering a redirect
// when several API calls 401 around the same time (e.g. at app boot).
let reLoginInFlight = false;

function AuthGate({ children, loginRequest }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    if (!isAuthenticated || accounts.length === 0) {
      setUser(null);
      setRoles([]);
      return;
    }
    const account = accounts[0];
    setUser({
      name: account.name || account.username,
      email: account.username,
    });
    const claims = account.idTokenClaims || {};
    setRoles(claims.roles || []);
  }, [isAuthenticated, accounts]);

  // Expired/invalid session (silent renewal failed, or the server rejected
  // the token as a 401) — force a single clean redirect to sign-in rather
  // than letting the app sit on a dead session. Guarded so concurrent
  // callers (several API calls failing around the same time) only redirect
  // once.
  const forceReLogin = useCallback(() => {
    if (reLoginInFlight) return;
    // Don't call loginRedirect while MSAL is still handling its startup
    // redirect — it throws interaction_in_progress and permanently blocks
    // the reLoginInFlight guard. Schedule a retry once MSAL is idle.
    if (inProgress !== InteractionStatus.None) {
      setTimeout(() => { reLoginInFlight = false; }, 1000);
      return;
    }
    reLoginInFlight = true;
    instance.loginRedirect(loginRequest).catch((err) => {
      reLoginInFlight = false; // reset so future attempts can retry
      console.warn('[auth] loginRedirect skipped:', err?.errorCode || err);
    });
  }, [instance, loginRequest, inProgress]);

  // Proactive session check: once MSAL finishes initialising, verify the
  // refresh token is still alive before any API calls fire. If dead the
  // user is redirected immediately instead of seeing a flash of 401 errors.
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    if (!isAuthenticated || accounts.length === 0) return;
    instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] })
      .catch((err) => {
        if (err instanceof InteractionRequiredAuthError) forceReLogin();
      });
  }, [inProgress]); // intentional: only re-run when MSAL interaction status changes

  const getAccessToken = useCallback(async () => {
    if (!isAuthenticated || accounts.length === 0) return null;
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return response.idToken;
    } catch (err) {
      // Whatever the failure mode (InteractionRequiredAuthError, a stale/
      // corrupt cached account, a half-finished redirect, ...), silent
      // renewal didn't produce a token for an account MSAL still thinks is
      // signed in. Letting this fall through silently just left the app
      // stuck issuing unauthenticated requests forever (401s with no
      // recovery) — force a clean re-login instead.
      console.warn('[auth] acquireTokenSilent failed:', err?.errorCode || err?.message || err);
      forceReLogin();
      return null;
    }
  }, [instance, accounts, isAuthenticated, loginRequest, forceReLogin]);

  const login = useCallback(() => {
    instance.loginRedirect(loginRequest);
  }, [instance, loginRequest]);

  const logout = useCallback(() => {
    instance.logoutRedirect();
  }, [instance]);

  const isAdmin = useMemo(() => roles.includes('PC-Admin'), [roles]);
  const isUser = useMemo(() => roles.includes('PC-User') || isAdmin, [roles, isAdmin]);

  const value = useMemo(() => ({
    user,
    roles,
    isAdmin,
    isUser,
    getAccessToken,
    login,
    logout,
    forceReLogin,
    authEnabled: true,
  }), [user, roles, isAdmin, isUser, getAccessToken, login, logout, forceReLogin]);

  if (!isAuthenticated) {
    return (
      <div className="auth-login-screen">
        <div className="auth-login-box">
          <h1>Water Haul Tracker</h1>
          <p>Sign in with your organization account to continue.</p>
          <button className="auth-login-btn" onClick={login}>
            Sign in with Microsoft
          </button>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

function NoAuthProvider({ children }) {
  const value = useMemo(() => ({
    user: null,
    roles: [],
    isAdmin: true,
    isUser: true,
    getAccessToken: async () => null,
    login: () => {},
    logout: () => {},
    forceReLogin: () => {},
    authEnabled: false,
  }), []);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default function AuthProvider({ children }) {
  const [state, setState] = useState({ loading: true, enabled: false, msalInstance: null, loginRequest: null });

  useEffect(() => {
    fetchAuthConfig().then(async (cfg) => {
      if (cfg.enabled && cfg.tenantId && cfg.clientId) {
        const msalInstance = new PublicClientApplication(buildMsalConfig(cfg.tenantId, cfg.clientId));
        // MSAL requires initialize() to finish before any instance method
        // (including the ones msal-react's hooks call internally, like
        // getAllAccounts) is used — skipping this throws
        // uninitialized_public_client_application and makes the app think
        // no account is signed in, even right after a successful login.
        await msalInstance.initialize();
        const loginRequest = buildLoginRequest();
        setState({ loading: false, enabled: true, msalInstance, loginRequest });
      } else {
        setState({ loading: false, enabled: false, msalInstance: null, loginRequest: null });
      }
    });
  }, []);

  if (state.loading) {
    return <div className="loading"><div className="loading-spinner" />Loading...</div>;
  }

  if (!state.enabled) {
    return <NoAuthProvider>{children}</NoAuthProvider>;
  }

  return (
    <MsalProvider instance={state.msalInstance}>
      <AuthGate loginRequest={state.loginRequest}>{children}</AuthGate>
    </MsalProvider>
  );
}
